import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Lock, Globe, UserCheck, Layers, Coins, ShieldOff, Crown, Sparkles,
  Loader2, Check, X, UserX,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import Avatar from '../components/Avatar';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';
import usePrivateProfileSettings from '../hooks/usePrivateProfileSettings';
import api from '../api/client';

const ComingSoonBadge = ({ t }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.05] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
    data-testid="coming-soon-badge"
  >
    <Sparkles className="h-3 w-3" />
    {t('badges.coming_soon')}
  </span>
);

const ActiveBadge = ({ t }) => (
  <span className="inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
    {t('badges.active_now')}
  </span>
);

const FeatureCard = ({ icon: Icon, title, badge, children, testId, highlight }) => (
  <div
    className={`rounded-2xl border p-5 transition-colors duration-300 md:p-6 ${
      highlight
        ? 'border-amber-500/35 bg-amber-500/[0.05]'
        : 'border-border bg-card hover:border-amber-500/25'
    }`}
    data-testid={testId}
  >
    <div className="mb-3 flex items-start justify-between gap-2">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10">
        <Icon className="h-5 w-5 gold-text" strokeWidth={1.8} />
      </div>
      {badge}
    </div>
    <h3 className="text-base font-semibold md:text-lg">{title}</h3>
    <div className="mt-2 space-y-1.5 text-sm leading-relaxed text-muted-foreground">
      {children}
    </div>
  </div>
);

/** Creator controls: private toggle + (VIP) paid access + price scale. */
const SettingsPanel = ({ t, settings, saving, save, toast }) => {
  const [priceOptions, setPriceOptions] = useState([]);
  const isVip = settings.is_vip_creator;
  const isPrivate = settings.visibility === 'private';

  useEffect(() => {
    api.get('/config/creator-price-options', { params: { scope: 'profile_access' } })
      .then(({ data }) => setPriceOptions((data || []).filter((o) => o.credits > 0)))
      .catch(() => {});
  }, []);

  const patch = async (p) => {
    const res = await save(p);
    if (res.ok) {
      toast({ title: t('settings.saved') });
    } else {
      toast({
        title: t('settings.save_error'),
        description: String(res.error || '').split('|').pop(),
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="mb-8 rounded-2xl border border-amber-500/25 bg-card p-5 md:p-6" data-testid="pp-settings-panel">
      <h2 className="mb-4 flex items-center gap-2 text-base font-semibold md:text-lg">
        <Lock className="h-4 w-4 gold-text" /> {t('settings.title')}
      </h2>

      <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background/40 p-4">
        <div>
          <p className="text-sm font-semibold">{t('settings.visibility_label')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('settings.visibility_hint')}</p>
        </div>
        <Switch
          checked={isPrivate}
          disabled={saving}
          onCheckedChange={(v) => patch({ visibility: v ? 'private' : 'public' })}
          data-testid="pp-visibility-switch"
        />
      </div>

      {isPrivate && (
        <div className={`mt-3 rounded-xl border p-4 ${isVip ? 'border-amber-500/30 bg-amber-500/[0.04]' : 'border-border bg-background/40 opacity-70'}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-semibold">
                <Coins className="h-4 w-4 gold-text" /> {t('settings.paid_label')}
                {!isVip && (
                  <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase gold-text">
                    {t('settings.vip_needed')}
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('settings.paid_hint')}</p>
            </div>
            <Switch
              checked={!!settings.paid_access_enabled}
              disabled={saving || !isVip}
              onCheckedChange={(v) => patch({ paid_access_enabled: v })}
              data-testid="pp-paid-access-switch"
            />
          </div>

          {isVip && settings.paid_access_enabled && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p className="text-xs font-semibold">{t('settings.price_label')}</p>
              <Select
                value={settings.paid_access_price_credits > 0 ? String(settings.paid_access_price_credits) : undefined}
                onValueChange={(v) => patch({ paid_access_price_credits: parseInt(v, 10) })}
                disabled={saving}
              >
                <SelectTrigger className="h-9 w-56" data-testid="pp-price-select">
                  <SelectValue placeholder={t('settings.price_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {priceOptions.map((o) => (
                    <SelectItem key={o.id} value={String(o.credits)} data-testid={`pp-price-option-${o.credits}`}>
                      {o.credits} {t('settings.credits')} (≈ €{o.eur_estimate.toFixed(2)}){o.label ? ` · ${o.label}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

/** Pending requests (approve/reject) + approved members (revoke). */
const RequestsPanel = ({ t, toast }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState('pending');

  const load = async () => {
    try {
      const { data } = await api.get('/me/profile-access/requests');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action, toastKey) => {
    setBusyId(id);
    try {
      await api.post(`/me/profile-access/requests/${id}/${action}`);
      toast({ title: t(`requests.${toastKey}`) });
      await load();
    } catch (e) {
      toast({
        title: t('settings.save_error'),
        description: String(e?.response?.data?.detail || '').split('|').pop(),
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const pending = rows.filter((r) => r.status === 'pending');
  const members = rows.filter((r) => r.status === 'approved');
  const list = tab === 'pending' ? pending : members;

  return (
    <section className="mb-8 rounded-2xl border border-border bg-card p-5 md:p-6" data-testid="pp-requests-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
          <UserCheck className="h-4 w-4 gold-text" /> {t('requests.title')}
        </h2>
        <div className="flex gap-1.5">
          <button
            onClick={() => setTab('pending')}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${tab === 'pending' ? 'gold-gradient text-black' : 'bg-secondary text-muted-foreground'}`}
            data-testid="pp-requests-tab-pending"
          >
            {t('requests.pending_tab')} ({pending.length})
          </button>
          <button
            onClick={() => setTab('members')}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${tab === 'members' ? 'gold-gradient text-black' : 'bg-secondary text-muted-foreground'}`}
            data-testid="pp-requests-tab-members"
          >
            {t('requests.members_tab')} ({members.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin gold-text" /></div>
      ) : list.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground" data-testid="pp-requests-empty">
          {tab === 'pending' ? t('requests.empty') : t('requests.members_empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3" data-testid={`pp-request-row-${r.id}`}>
              <Avatar src={r.requester?.avatar_url} alt={r.requester?.username} size={36} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">@{r.requester?.username}</p>
                <p className="text-[11px] text-muted-foreground">
                  {r.price_credits > 0
                    ? (r.status === 'pending'
                      ? t('requests.escrow_note', { amount: r.price_credits })
                      : t('requests.paid_note', { amount: r.price_credits }))
                    : t('requests.free_note')}
                </p>
              </div>
              {r.status === 'pending' ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => act(r.id, 'approve', 'approved_toast')}
                    className="h-8 bg-emerald-600 text-white hover:bg-emerald-500"
                    data-testid={`pp-approve-${r.id}`}
                  >
                    <Check className="mr-1 h-3.5 w-3.5" /> {t('requests.approve')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busyId === r.id}
                    onClick={() => act(r.id, 'reject', 'rejected_toast')}
                    className="h-8 text-red-400 hover:text-red-300"
                    data-testid={`pp-reject-${r.id}`}
                  >
                    <X className="mr-1 h-3.5 w-3.5" /> {t('requests.reject')}
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busyId === r.id}
                  onClick={() => act(r.id, 'revoke', 'revoked_toast')}
                  className="h-8 text-red-400 hover:text-red-300"
                  data-testid={`pp-revoke-${r.id}`}
                >
                  <UserX className="mr-1 h-3.5 w-3.5" /> {t('requests.revoke')}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default function PrivateProfilePage() {
  const { t } = useTranslation('privateProfile');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { settings, saving, save } = usePrivateProfileSettings();

  const isVip = settings ? settings.is_vip_creator : !!user?.is_verified_creator;
  const isPrivate = settings?.visibility === 'private';
  const soon = <ComingSoonBadge t={t} />;
  const active = <ActiveBadge t={t} />;

  return (
    <div className="mx-auto max-w-4xl px-4 pb-16 pt-6 md:px-6 md:pt-10" data-testid="private-profile-page">
      {/* Header */}
      <header className="mb-8 md:mb-10">
        <div className="mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5 gold-text" strokeWidth={1.8} />
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
            {t('nav_label')}
          </span>
        </div>
        <h1 className="text-3xl font-bold leading-tight sm:text-4xl" data-testid="private-profile-title">
          {t('page.title')}
        </h1>
        <div className="mt-4 max-w-2xl space-y-1.5 text-sm leading-relaxed text-muted-foreground md:text-base">
          <p>{t('page.intro_1')}</p>
          <p>{t('page.intro_4')}</p>
        </div>
      </header>

      {/* Phase 2 — live controls */}
      {settings && (
        <SettingsPanel t={t} settings={settings} saving={saving} save={save} toast={toast} />
      )}
      {settings && isPrivate && <RequestsPanel t={t} toast={toast} />}

      {/* Le 5 card */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FeatureCard
          icon={Globe}
          title={t('cards.public.title')}
          testId="pp-card-public"
          badge={!isPrivate ? active : null}
        >
          <p>{t('cards.public.desc_1')}</p>
          <p>{t('cards.public.desc_2')}</p>
        </FeatureCard>

        <FeatureCard
          icon={Lock}
          title={t('cards.private.title')}
          badge={isPrivate ? active : null}
          testId="pp-card-private"
          highlight={isPrivate}
        >
          <p>{t('cards.private.desc_1')}</p>
          <p>{t('cards.private.desc_2')}</p>
        </FeatureCard>

        <FeatureCard icon={UserCheck} title={t('cards.approval.title')} badge={isPrivate ? active : null} testId="pp-card-approval">
          <p>{t('cards.approval.desc_1')}</p>
          <div className="flex gap-2 py-1">
            <span className="rounded-full border border-emerald-500/30 px-3 py-0.5 text-xs text-emerald-400">{t('cards.approval.approve')}</span>
            <span className="rounded-full border border-red-500/30 px-3 py-0.5 text-xs text-red-400">{t('cards.approval.reject')}</span>
          </div>
          <p>{t('cards.approval.desc_2')}</p>
        </FeatureCard>

        <FeatureCard icon={Layers} title={t('cards.reserved.title')} badge={soon} testId="pp-card-reserved">
          <p>{t('cards.reserved.desc_1')}</p>
          <ul className="space-y-1 pt-1">
            {['level_public', 'level_followers', 'level_premium'].map((k) => (
              <li key={k} className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-amber-500/70" />
                {t(`cards.reserved.${k}`)}
              </li>
            ))}
          </ul>
        </FeatureCard>

        <FeatureCard
          icon={Coins}
          title={t('cards.paid.title')}
          testId="pp-card-paid"
          badge={(
            <div className="flex flex-col items-end gap-1.5">
              {settings?.paid_access_enabled ? active : null}
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider gold-text">
                <Crown className="h-3 w-3" /> {t('badges.vip_only')}
              </span>
            </div>
          )}
        >
          <p>{t('cards.paid.desc_1')}</p>
          <p>{t('cards.paid.desc_2')}</p>
        </FeatureCard>

        <FeatureCard icon={ShieldOff} title={t('cards.revoke.title')} badge={isPrivate ? active : null} testId="pp-card-revoke">
          <p>{t('cards.revoke.desc_1')}</p>
          <p>{t('cards.revoke.desc_2')}</p>
        </FeatureCard>
      </div>

      {/* Box VIP per creator non ancora VIP */}
      {!isVip && !hasActiveCreatorProfile(user) && (
        <div
          className="mt-8 overflow-hidden rounded-2xl border border-amber-500/35 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-6 md:p-8"
          data-testid="pp-vip-box"
        >
          <div className="flex flex-col items-start gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <h2 className="flex items-center gap-2 text-xl font-bold md:text-2xl">
                <Crown className="h-5 w-5 gold-text" />
                {t('vip_box.title')}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base">
                {t('vip_box.text')}
              </p>
            </div>
            <Button
              onClick={() => navigate('/creator/onboarding')}
              className="gold-gradient shrink-0 rounded-full px-6 py-5 text-sm font-bold text-black"
              data-testid="pp-vip-cta"
            >
              {t('vip_box.cta')}
            </Button>
          </div>
        </div>
      )}

      <p className="mt-10 text-center text-sm italic leading-relaxed text-muted-foreground" data-testid="pp-footer-note">
        {t('footer_note')}
      </p>
    </div>
  );
}
